import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Alert, Spinner, Table } from "react-bootstrap";
import moment from "moment";

import { flattenAddress } from "../../../../utils/address";

const formatWindowLabel = (window) => {
  const ready = moment(window.ready).format("HH:mm");
  const deadline = moment(window.deadline).format("HH:mm");
  return `${ready} - ${deadline}`;
};

const formatOrderSlot = (order) => {
  if (!order) {
    return null;
  }

  const start = order.shipping_time
    ? moment(order.shipping_time).format("HH:mm")
    : null;
  const end = order.max_shipping_time
    ? moment(order.max_shipping_time).format("HH:mm")
    : null;

  if (!start && !end) {
    return null;
  }

  if (start && end) {
    return `${start} - ${end}`;
  }

  return start || end;
};

const UberDirectBatchModal = ({ modal, onHide, onCreateBatch }) => {
  const [selectedWindowIdx, setSelectedWindowIdx] = useState("");

  // Hooks must be called unconditionally - reset selection when modal opens/closes or dock changes
  useEffect(() => {
    setSelectedWindowIdx("");
  }, [modal && modal.modalType, modal && modal.dock && modal.dock.id]);

  if (!modal || modal.modalType !== "uberdirect_batch") {
    return null;
  }

  const { dock, loadingWindows, windows, error, sending } = modal;

  const selectedWindow =
    (selectedWindowIdx !== "" &&
      windows &&
      windows[Number(selectedWindowIdx)]) ||
    null;

  const handleConfirm = () => {
    if (!selectedWindow) {
      return;
    }
    onCreateBatch(dock.id, selectedWindow.ready, selectedWindow.deadline);
  };

  const handleClose = () => {
    setSelectedWindowIdx("");
    onHide();
  };

  return (
    <Modal
      show={modal.modalType === "uberdirect_batch"}
      onHide={handleClose}
      animation={false}
      backdrop
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>UberDirect Batch - {dock ? dock.name : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingWindows ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="sr-only">Chargement des créneaux...</span>
            </Spinner>
            <p className="mt-2">Chargement des créneaux disponibles...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">Erreur: {error}</Alert>
        ) : (
          <>
            {/* Dock orders table */}
            {dock && dock.tasks && dock.tasks.length > 0 && (
              <div className="mb-3">
                <label className="font-weight-bold">
                  Commandes du groupement ({dock.tasks.length}) :
                </label>
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Commande</th>
                      <th>Créneau</th>
                      <th>Adresse</th>
                      <th>Poids</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dock.tasks.map((task, idx) => (
                      <tr key={task.id || idx}>
                        <td className="text-nowrap">
                          <strong>
                            #
                            {task.linked_order
                              ? task.linked_order.random_id
                              : ""}
                          </strong>
                        </td>
                        <td className="text-nowrap">
                          {task.linked_order &&
                            formatOrderSlot(task.linked_order) ? (
                            formatOrderSlot(task.linked_order)
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>
                          <small>
                            {task.address ? flattenAddress(task.address) : "—"}
                          </small>
                        </td>
                        <td className="text-right text-nowrap">
                          <small className="text-muted">
                            {task.linked_order
                              ? task.linked_order.total_weight
                              : "—"}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {/* Summary */}
            <div className="mb-3">
              <ul className="list-unstyled">
                <li>
                  <strong>Nombre de commandes :</strong>{" "}
                  {dock && dock.tasks ? dock.tasks.length : 0}
                </li>
              </ul>
            </div>

            {/* Window selector */}
            <div className="mb-4">
              <label className="font-weight-bold">
                Créneau de livraison UberDirect possible:
              </label>
              {windows && windows.length > 0 ? (
                <>
                  <Alert variant="info">
                    L'appel à UberDirect sera fait 2h30 avant le début du
                    créneau sélectionné.
                  </Alert>
                  <select
                    className="form-control"
                    value={selectedWindowIdx}
                    onChange={(e) => setSelectedWindowIdx(e.target.value)}
                    disabled={sending}
                  >
                    <option value="">Sélectionner un créneau</option>
                    {windows.map((window, idx) => (
                      <option key={idx} value={String(idx)}>
                        {formatWindowLabel(window)}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <Alert variant="warning" className="mt-2">
                  Aucun créneau uberdirect batch possible
                </Alert>
              )}
            </div>
            {!!selectedWindowIdx && (
              <Alert variant="warning">
                Après validation, il ne sera plus possible de modifier ce
                groupement et UberDirect pourra modifier l'ordre des livraisons.
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Fermer
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={handleConfirm}
          disabled={
            loadingWindows ||
            sending ||
            !selectedWindow ||
            (windows && windows.length === 0)
          }
        >
          {sending ? "Création en cours..." : "Créer le batch UberDirect"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

UberDirectBatchModal.propTypes = {
  modal: PropTypes.object,
  onHide: PropTypes.func,
  onCreateBatch: PropTypes.func,
};

export default UberDirectBatchModal;
